<template>
  <div class="dtf" :class="{ 'dtf--invalid': invalid }" :title="title || null">
    <label v-if="label" class="dtf__label">{{ label }}</label>
    <div class="dtf__row">
      <input v-if="!timeOnly" class="dtf__date" type="date" :value="datePart" :disabled="disabled" @input="onDate($event.target.value)" />
      <button ref="btn" type="button" class="dtf__time" :class="{ 'is-empty': !timePart, 'is-open': opened }" :disabled="disabled" aria-haspopup="dialog" :aria-expanded="opened ? 'true' : 'false'" @click="toggle">
        <i class="far fa-clock"></i>
        <span>{{ timePart || '--:--' }}</span>
      </button>
      <button v-if="value && !disabled" type="button" class="dtf__clear" title="Очистить" aria-label="Очистить" @click="clear">
        <i class="fas fa-times"></i>
      </button>
    </div>

    <!-- Циферблат: сначала час (внешнее кольцо 00 и 13–23, внутреннее 12 и 1–11), потом минуты -->
    <div v-if="opened" ref="pop" class="dtf-pop" :style="popStyle" role="dialog" aria-label="Выбор времени">
      <div class="dtf-pop__head">
        <button type="button" class="dtf-seg" :class="{ 'is-active': mode === 'hour' }" @click="mode = 'hour'">{{ pad(hour) }}</button>
        <span class="dtf-colon">:</span>
        <button type="button" class="dtf-seg" :class="{ 'is-active': mode === 'minute' }" @click="mode = 'minute'">{{ pad(minute) }}</button>
      </div>

      <svg
        ref="dial"
        class="dtf-dial"
        viewBox="0 0 240 240"
        @pointerdown="onDown"
        @pointermove="onMove"
        @pointerup="onUp"
        @pointercancel="onUp"
      >
        <circle cx="120" cy="120" r="116" class="dtf-dial__face" />
        <text
          v-for="l in labels"
          :key="l.key"
          :x="l.x"
          :y="l.y"
          class="dtf-dial__label"
          :class="{ 'is-inner': l.inner }"
          text-anchor="middle"
          dominant-baseline="central"
        >{{ l.text }}</text>
        <line x1="120" y1="120" :x2="hand.x" :y2="hand.y" class="dtf-dial__hand" />
        <circle cx="120" cy="120" r="3.5" class="dtf-dial__center" />
        <circle :cx="hand.x" :cy="hand.y" :r="hand.r" class="dtf-dial__knob" />
        <text :x="hand.x" :y="hand.y" class="dtf-dial__knob-text" text-anchor="middle" dominant-baseline="central">{{ hand.text }}</text>
      </svg>

      <div class="dtf-pop__foot">
        <button type="button" class="dtf-link" @click="setNow">Сейчас</button>
        <button type="button" class="dtf-done" @click="close">Готово</button>
      </div>
    </div>
  </div>
</template>

<script>
import { dayString, toInputDateTime } from '@/time'

const POP_WIDTH = 288
const POP_HEIGHT = 400
const OUTER = 96
const INNER = 62

const pad = (n) => (n < 10 ? '0' : '') + n

// Положение на циферблате: угол по часовой стрелке от верхней точки
function polar(angleDeg, radius) {
  const a = (angleDeg * Math.PI) / 180
  return { x: Math.round((120 + radius * Math.sin(a)) * 10) / 10, y: Math.round((120 - radius * Math.cos(a)) * 10) / 10 }
}

// Поле «дата и время»: дата выбирается как обычно, время — на круглом циферблате.
// Значение — строка вида 2026-10-15T14:00 (как у datetime-local, время по Екатеринбургу).
export default {
  name: 'DateTimeField',
  model: { prop: 'value', event: 'input' },
  props: {
    value: { type: String, default: '' },
    label: { type: String, default: '' },
    title: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    invalid: { type: Boolean, default: false },
    // Выбор даты без времени не дописывает 00:00: значение остаётся датой («2026-10-23»), время вводится вручную
    emptyTime: { type: Boolean, default: false },
    // Только время: значение — «ЧЧ:ММ» (без даты), поле даты не показывается
    timeOnly: { type: Boolean, default: false }
  },
  data() {
    return { opened: false, mode: 'hour', dragging: false, popStyle: {} }
  },
  computed: {
    datePart() {
      return /^\d{4}-\d{2}-\d{2}/.test(this.value || '') ? this.value.slice(0, 10) : ''
    },
    timePart() {
      if (this.timeOnly) return /^\d{2}:\d{2}/.test(this.value || '') ? this.value.slice(0, 5) : ''
      return /T\d{2}:\d{2}/.test(this.value || '') ? this.value.slice(11, 16) : ''
    },
    // пока время не выбрано, циферблат стоит на 12:00
    hour() {
      return this.timePart ? parseInt(this.timePart.slice(0, 2), 10) : 12
    },
    minute() {
      return this.timePart ? parseInt(this.timePart.slice(3, 5), 10) : 0
    },
    labels() {
      const list = []
      if (this.mode === 'hour') {
        for (let k = 0; k < 12; k++) {
          const outer = polar(k * 30, OUTER)
          list.push({ key: 'o' + k, text: pad(k === 0 ? 0 : 12 + k), x: outer.x, y: outer.y, inner: false })
          const inner = polar(k * 30, INNER)
          list.push({ key: 'i' + k, text: String(k === 0 ? 12 : k), x: inner.x, y: inner.y, inner: true })
        }
      } else {
        for (let m = 0; m < 60; m += 5) {
          const p = polar(m * 6, OUTER)
          list.push({ key: 'm' + m, text: pad(m), x: p.x, y: p.y, inner: false })
        }
      }
      return list
    },
    // Стрелка и кружок выбранного значения
    hand() {
      if (this.mode === 'hour') {
        const h = this.hour
        const inner = h >= 1 && h <= 12
        const k = (inner ? h : h - 12) % 12
        const p = polar(k * 30, inner ? INNER : OUTER)
        return { x: p.x, y: p.y, r: 17, text: inner ? String(h) : pad(h) }
      }
      const p = polar(this.minute * 6, OUTER)
      return { x: p.x, y: p.y, r: 17, text: pad(this.minute) }
    }
  },
  beforeDestroy() {
    this.detach()
    // окно вынесено в body и само не удалится, если поле исчезло с открытым циферблатом (закрыли страницу или окно)
    const pop = this.$refs.pop
    if (pop && pop.parentNode) pop.parentNode.removeChild(pop)
  },
  methods: {
    pad,
    emit(date, hour, minute) {
      if (this.timeOnly) {
        this.$emit('input', pad(hour) + ':' + pad(minute))
        return
      }
      this.$emit('input', (date || dayString()) + 'T' + pad(hour) + ':' + pad(minute))
    },
    // Дата выбрана без времени — время 00:00 (в плане это «дата без времени»)
    onDate(day) {
      this.$emit('input', day ? (this.timePart || !this.emptyTime ? day + 'T' + (this.timePart || '00:00') : day) : '')
    },
    clear() {
      this.$emit('input', '')
      this.close()
    },
    setNow() {
      const now = toInputDateTime(new Date())
      this.$emit('input', this.timeOnly ? now.slice(11, 16) : now)
    },

    toggle() {
      if (this.opened) this.close()
      else this.open()
    },
    open() {
      if (this.disabled) return
      this.mode = 'hour'
      this.opened = true
      this.$nextTick(() => {
        this.place()
        // окно выносится в body: внутри окон с прокруткой и анимацией оно обрезалось бы
        if (this.$refs.pop) document.body.appendChild(this.$refs.pop)
        document.addEventListener('mousedown', this.onOutside, true)
        document.addEventListener('keydown', this.onKey, true)
        window.addEventListener('resize', this.close)
      })
    },
    close() {
      this.opened = false
      this.dragging = false
      this.detach()
    },
    detach() {
      document.removeEventListener('mousedown', this.onOutside, true)
      document.removeEventListener('keydown', this.onKey, true)
      window.removeEventListener('resize', this.close)
    },
    place() {
      const btn = this.$refs.btn
      if (!btn) return
      const rect = btn.getBoundingClientRect()
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - POP_WIDTH - 8))
      let top = rect.bottom + 6
      if (top + POP_HEIGHT > window.innerHeight) top = Math.max(8, rect.top - POP_HEIGHT - 6)
      this.popStyle = { left: left + 'px', top: top + 'px' }
    },
    onOutside(e) {
      const pop = this.$refs.pop
      if ((pop && pop.contains(e.target)) || (this.$refs.btn && this.$refs.btn.contains(e.target))) return
      this.close()
    },
    // Клавиатура: Esc и Enter закрывают, стрелки меняют час или минуты
    onKey(e) {
      if (e.key === 'Escape' || e.key === 'Enter') {
        e.preventDefault()
        e.stopPropagation()
        this.close()
        return
      }
      const step = { ArrowUp: 1, ArrowRight: 1, ArrowDown: -1, ArrowLeft: -1 }[e.key]
      if (!step) return
      e.preventDefault()
      if (this.mode === 'hour') this.emit(this.datePart, (this.hour + step + 24) % 24, this.minute)
      else this.emit(this.datePart, this.hour, (this.minute + step + 60) % 60)
    },

    // Выбор на циферблате: касание или перетаскивание
    pointAt(e) {
      const rect = this.$refs.dial.getBoundingClientRect()
      const scale = 240 / rect.width
      const x = (e.clientX - rect.left) * scale - 120
      const y = (e.clientY - rect.top) * scale - 120
      return { dist: Math.hypot(x, y), angle: ((Math.atan2(x, -y) * 180) / Math.PI + 360) % 360 }
    },
    apply(e) {
      const { dist, angle } = this.pointAt(e)
      if (this.mode === 'hour') {
        const k = Math.round(angle / 30) % 12
        const inner = dist < (OUTER + INNER) / 2 + 2
        this.emit(this.datePart, inner ? (k === 0 ? 12 : k) : (k === 0 ? 0 : 12 + k), this.minute)
      } else {
        this.emit(this.datePart, this.hour, Math.round(angle / 6) % 60)
      }
    },
    onDown(e) {
      this.dragging = true
      if (e.target.setPointerCapture && e.pointerId !== undefined) {
        try {
          this.$refs.dial.setPointerCapture(e.pointerId)
        } catch (err) {
          // без захвата перетаскивание работает, пока курсор над циферблатом
        }
      }
      this.apply(e)
    },
    onMove(e) {
      if (this.dragging) this.apply(e)
    },
    onUp() {
      if (!this.dragging) return
      this.dragging = false
      // час выбран — дальше минуты
      if (this.mode === 'hour') this.mode = 'minute'
    }
  }
}
</script>

<style scoped>
.dtf__label {
  display: block;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
  margin-bottom: var(--space-2);
}
.dtf__row { display: flex; align-items: stretch; gap: var(--space-4); }
.dtf__date,
.dtf__time {
  padding: var(--space-4) var(--space-8);
  border-radius: var(--radius-input);
  border: var(--border-width-default) var(--border-style-base) var(--border-default);
  font-size: var(--text-base);
  font-family: inherit;
  background: var(--bg-input);
  color: var(--text-primary);
}
.dtf__date { flex: 1; min-width: 0; }
.dtf__time {
  display: inline-flex;
  align-items: center;
  gap: var(--space-4);
  flex-shrink: 0;
  cursor: pointer;
  font-variant-numeric: tabular-nums;
}
.dtf__time i { color: var(--text-tertiary); }
.dtf__time.is-empty span { color: var(--text-tertiary); }
.dtf__time:hover:not(:disabled), .dtf__time.is-open { border-color: var(--border-focus); background: var(--bg-surface); }
.dtf__date:focus { outline: none; border-color: var(--border-focus); background: var(--bg-surface); }
.dtf__date:disabled, .dtf__time:disabled { opacity: 0.6; cursor: not-allowed; }
.dtf--invalid .dtf__date, .dtf--invalid .dtf__time { border-color: var(--danger); }
.dtf__clear {
  flex-shrink: 0;
  width: 2rem;
  border: none;
  border-radius: var(--radius-input);
  background: none;
  color: var(--text-tertiary);
  cursor: pointer;
}
.dtf__clear:hover { background: var(--bg-subtle); color: var(--danger-text); }

/* Окно с циферблатом */
.dtf-pop {
  position: fixed;
  z-index: 2000;
  width: 288px;
  padding: var(--space-8);
  background: var(--bg-surface);
  border: var(--border-width-default) solid var(--border-default);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xl);
  user-select: none;
  -webkit-user-select: none;
}
.dtf-pop__head { display: flex; align-items: center; justify-content: center; gap: var(--space-2); margin-bottom: var(--space-6); }
.dtf-colon { font-size: 2.4rem; font-weight: 300; line-height: 1; color: var(--text-secondary); }
.dtf-seg {
  min-width: 4.2rem;
  padding: var(--space-2) var(--space-4);
  border: none;
  border-radius: var(--radius-md);
  background: var(--bg-subtle);
  color: var(--text-secondary);
  font-family: inherit;
  font-size: 2.4rem;
  font-weight: 300;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
  cursor: pointer;
}
.dtf-seg.is-active { background: var(--primary-soft); color: var(--primary); }

.dtf-dial { display: block; width: 100%; height: auto; touch-action: none; cursor: pointer; }
.dtf-dial__face { fill: var(--bg-subtle); }
.dtf-dial__label { fill: var(--text-primary); font-size: 14px; pointer-events: none; }
.dtf-dial__label.is-inner { font-size: 12px; fill: var(--text-secondary); }
.dtf-dial__hand { stroke: var(--primary); stroke-width: 2; pointer-events: none; }
.dtf-dial__center { fill: var(--primary); pointer-events: none; }
.dtf-dial__knob { fill: var(--primary); pointer-events: none; }
.dtf-dial__knob-text { fill: #fff; font-size: 13px; font-weight: 600; pointer-events: none; }

.dtf-pop__foot { display: flex; align-items: center; justify-content: space-between; margin-top: var(--space-6); }
.dtf-link { border: none; background: none; color: var(--primary); font-family: inherit; font-size: var(--text-sm); font-weight: var(--font-weight-semibold); cursor: pointer; }
.dtf-link:hover { text-decoration: underline; }
.dtf-done {
  padding: var(--space-4) var(--space-12);
  border: none;
  border-radius: var(--radius-md);
  background: var(--primary);
  color: #fff;
  font-family: inherit;
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
}
.dtf-done:hover { filter: brightness(0.95); }
</style>
